import { sym } from '#library/symbol.library.js';
import { unwrap } from '#library/primitive.library.js';
const registry = []; // global types for getType
/**
 * Returns the primitive type reported by Object.prototype.toString / Symbol.toStringTag.
 *
 * Performance: Short-circuits all primitives via typeof in ~0.5ns with zero allocations.
 * Safely wrapped in a try-catch to guard against:
 * 1. Throwing dynamic getters on Symbol.toStringTag (e.g. `{ get [Symbol.toStringTag]() { throw ... } }`)
 * 2. Revoked Proxies (`Proxy.revocable()`) which throw TypeError on property/tag access
 * 3. Cross-origin / cross-realm security exceptions in browser environments (DOMException)
 */
export const protoType = (obj) => {
    if (obj === null)
        return 'Null';
    if (obj === undefined)
        return 'Undefined';
    const t = typeof obj;
    if (t === 'string')
        return 'String';
    if (t === 'number')
        return 'Number';
    if (t === 'boolean')
        return 'Boolean';
    if (t === 'bigint')
        return 'BigInt';
    if (t === 'symbol')
        return 'Symbol';
    try {
        const raw = (t === 'object') ? unwrap(obj) : obj;
        return Object.prototype.toString.call(raw).slice(8, -1);
    }
    catch {
        return (t === 'function' ? 'Function' : 'Object');
    }
};
/** Safely extract Symbol.toStringTag, guarding against dynamic getters that throw */
export const getSafeTag = (obj) => {
    try {
        const tag = obj?.[Symbol.toStringTag] ?? obj?.prototype?.[Symbol.toStringTag];
        if (typeof tag === 'string')
            return tag;
    }
    catch { }
    return undefined;
};
/**
 * Returns an object's type as a ProperCase string (e.g., 'String', 'Number', 'Array').
 * If the object is a registered class instance, returns its class name.
 *
 * @remarks
 * **Load-Order Dependency:** Consumers must import modules that call `registerType()`
 * before calling `getType()` to ensure custom types are correctly identified.
 *
 * @param obj - The value to determine the type of
 * @param instances - Optional list of additional custom instances to check against
 * @returns The string representation of the object's type
 * @example
 * ```ts
 * getType([]); // 'Array'
 * ```
 */
export const getType = (obj, ...instances) => {
    let raw = obj;
    try {
        raw = obj?.[sym.$Target] ?? obj; // bypass Proxy traps
    }
    catch {
        raw = obj; // Guard against revoked proxies or hostile traps
    }
    const type = protoType(raw);
    switch (type) {
        case 'Function':
            try {
                return isClassConstructor(raw) ? 'Class' : 'Function';
            }
            catch {
                return 'Function';
            }
        case 'Object': {
            try {
                // check for ArrayLike (e.g. {0:'a', 1:'b', length:2})
                if ('length' in raw && Object.keys(raw).every(key => key === 'length' || Number.isFinite(Number(key))))
                    return 'ArrayLike';
                for (const inst of instances) {
                    const instRaw = inst.class?.[sym.$Target] ?? inst.class;
                    if (raw === instRaw || (instRaw && raw instanceof instRaw))
                        return inst.type;
                }
                const globalRegistry = globalThis[sym.$Registry] ?? [];
                for (const inst of [...registry, ...globalRegistry]) {
                    const instRaw = inst.class?.[sym.$Target] ?? inst.class;
                    if (raw === instRaw || (instRaw && raw instanceof instRaw))
                        return inst.type;
                }
            }
            catch {
                return 'Object';
            }
            return 'Object';
        }
        default: return type;
    }
};
/**
 * Wraps a value in a TypeValue object containing both its determined type and the original value.
 *
 * @param value - The value to wrap
 * @param instances - Optional list of additional custom instances to check against for typing
 * @returns A TypeValue wrapper object
 * @example
 * ```ts
 * const wrapper = asType('hello'); // { type: 'String', value: 'hello' }
 * ```
 */
export const asType = (value, ...instances) => ({ type: getType(value, ...instances), value });
/**
 * Clears the global type registry.
 * Primarily used for test isolation and ensuring deterministic behavior between test suites.
 */
export const resetRegistry = () => {
    registry.length = 0;
    if (Array.isArray(globalThis[sym.$Registry])) {
        globalThis[sym.$Registry].length = 0;
    }
};
const classRegex = /^\s*(class\s|\[native code\])/; // match class keyword OR native constructor
/** private helper to safely identify an ES6 class, bypassing Proxies */
const isClassConstructor = (obj) => {
    if (typeof obj !== 'function')
        return false;
    const raw = obj?.[sym.$Target] ?? obj; // bypass Proxy traps
    // Arrow functions do NOT have a prototype property, whereas traditional functions and classes DO.
    // This is a high-performance check to immediately classify arrow functions as non-classes.
    if (!('prototype' in raw))
        return false;
    const name = raw?.name;
    const tag = getSafeTag(raw);
    // Absolute bypass for branded identities (using universal brand)
    if (raw?.[sym.$Identity] || raw?.prototype?.[sym.$Identity]) {
        return true;
    }
    if (typeof tag === 'string' && tag.startsWith('Temporal.')) {
        return true;
    }
    if (typeof tag === 'string' && tag.endsWith('Function'))
        return false; // check the tag directly to avoid misidentifying function as class
    const globalRegistry = globalThis[sym.$Registry] ?? [];
    if (globalRegistry.some((inst) => (inst.class?.[sym.$Target] ?? inst.class) === raw || (name && inst.type === name) || (tag && inst.type === tag))) {
        return true;
    }
    // 3. Last resort: Inspection of constructor property & prototype (Transpilation-Safe)
    try {
        const str = Function.prototype.toString.call(raw);
        if (classRegex.test(str)) {
            return true;
        }
        // ES6 classes have a non-writable prototype descriptor
        // Note: Object.freeze() also makes the prototype non-writable, so we exclude frozen objects.
        const descriptor = Object.getOwnPropertyDescriptor(raw, 'prototype');
        if (descriptor && descriptor.writable === false && !Object.isFrozen(raw)) {
            return true;
        }
        // if it's a function with a 'prototype' that is NOT an empty object (excluding its constructor), it's likely a class
        const proto = raw.prototype;
        if (proto && Object.getOwnPropertyNames(proto).length > 2) {
            return true;
        }
    }
    catch {
        return false;
    }
    return false;
};
/**
 * Registers a class constructor with the runtime type system.
 *
 * @remarks
 * Custom types must augment `TypeValueMap` to be strictly recognized by the TypeScript compiler.
 *
 * @param cls - The class constructor to register
 * @param type - Optional explicit type name to register the class under
 * @example
 * ```ts
 * registerType(MyCustomClass, 'MyCustomType');
 * ```
 */
export const registerType = (cls, type) => {
    if (typeof cls !== 'function')
        return;
    const tag = getSafeTag(cls);
    const name = (type ?? tag ?? cls.name);
    if (name && !['Object', 'Function', ''].includes(name)) {
        if (!registry.some(inst => inst.class === cls))
            registry.push({ class: cls, type: name });
    }
};
/**
 * Ergonomically coerces any runtime value to type `T`, eliminating `as unknown as T` / `as any as T` double-assertions.
 *
 * @param value - The value to cast
 * @returns The value typed as `T`
 * @example
 * ```ts
 * const instance = cast<Ticker.Instance>(proxy);
 * ```
 */
export const cast = (value) => value;
